import { createSlice, createAsyncThunk, type Dispatch, type PayloadAction } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import type { UUIDTypes } from "uuid";
import type { RootState } from "@/typedDispatch";
import type { IConnectionCustomFieldAndValue } from "../maintenance/maintenanceSlice";
import type { IConnectionInfo, IThunkConfig } from "../sharedTypes";

// temporary type for catalogues, the store is storing all catalogues in the same variable will need 
export interface ICatalog {
  [key: string]: UUIDTypes | string | number | IItemQualifiers | IConnectionCustomFieldAndValue[] | ICustomerArtifactData| null | undefined;
  CustomerId?: UUIDTypes;
  ShipToCode?: string;
  ItemId?: UUIDTypes;
  Code?: string;
  UOM?: string;
  ItemQualifiers?: IItemQualifiers;
  CustomFields?: IConnectionCustomFieldAndValue[];
  Customer? : ICustomerArtifactData;
}

export interface IItemQualifiers {
  [key: string]: string;
}

export interface IAddCatalogPopup {
  onClose: () => void;
  onValidate: (dataValue: ICatalog) => IValidationForm;
  onComplete: () => void;
}

export interface IDeleteCatalogDataAction {
  selectedMappings: {
    [key: string]: IChildCatalogIdAndCode[]
  };
}
export interface IChildCatalogIdAndCode {
  ParentId: UUIDTypes;
  Id: UUIDTypes;
  Reference: string;
}
export interface ICustomerArtifactData {
  ArtifactDictionaryDataId?: UUIDTypes | null;
  Reference: string;
  ArtifactData?: string;
  CustomerName?: string;
  CustomerCode?: string;
}
export interface IItemArtifactData {
  ArtifactDictionaryDataId?: UUIDTypes | null;
  Code: string;
  Description?: string;
}
export interface IValidationForm {
  isValid: boolean;
  message: string;
}
interface IManageCatalogDataSliceState {
  selectedConnection: number;
  displayName: string;
  selectedCatalogType: number;
  catalogTypes: number[];
  appType: number;
  rows: [],
  selectedRows: [],
  qualifiers: string[],
  connectionName: string;
  customer: ICustomerArtifactData | null,
  isEdiHandlingConnection: boolean;
  status: string;
  error: string | null,
  addEditInfo: {
    customers: ICustomerArtifactData[],
    items: IItemArtifactData[],
    isOpen: boolean;
    isEdit: boolean;
    connectionInfo: IConnectionInfo,
    catalogType: number;
    catalogId: UUIDTypes | null;
    catalogData: ICatalog,
  },
}
interface IConnectionNameAction
{
  connectionName: string;
  displayName: string;
}
const manageCatalogDataSlice = createSlice({
  name: "manageCatalogData",
  initialState: {
    selectedConnection: -1,
    displayName: "",
    selectedCatalogType: -1,
    catalogTypes: [],
    appType: -1,
    rows: [],
    selectedRows: [],
    qualifiers: [],
    connectionName: "",
    customer: null,
    isEdiHandlingConnection: false,
    status: "initial",
    error: null,
    addEditInfo: {
      customers: [],
      items: [],
      isOpen: false,
      isEdit: false,
      connectionInfo: { connectionDisplayName: "", connectionId: -1 },
      catalogType: -1,
      catalogId: null,
      catalogData: {},
    },
  } satisfies IManageCatalogDataSliceState as IManageCatalogDataSliceState,
  reducers: {
    setConnection: (state, action) => {
      const { selectedConnection } = action.payload;
      state.selectedConnection = selectedConnection;
    },
    setCatalogTypes: (state, action) => {
      const { catalogTypes } = action.payload;
      state.catalogTypes = catalogTypes;
    },
    setCatalogType: (state, action) => {
      const { catalogType } = action.payload;
      state.selectedCatalogType = catalogType;
    },
    setAppType: (state, action) => {
      const { appType } = action.payload;
      state.appType = appType;
    },
    setRowsData: (state, action) => {
      const { rows } = action.payload;
      state.rows = rows;
    },
    setSelectedRow: (state, action) => {
      const { rows } = action.payload;
      state.selectedRows = rows;
    },
    setConnectionName: (state, action: PayloadAction<IConnectionNameAction>) => {
      const { connectionName, displayName } = action.payload;
      state.connectionName = connectionName;
      state.displayName = displayName;
    },
    setCustomer: (state, action) => {
      const { customer } = action.payload;
      state.customer = customer;
    },
    setIsAddCatalogOpen: (state, action) => {
      const { isOpen, isEdit, connectionInfo, catalogType } = action.payload;
      state.addEditInfo.isOpen = isOpen;
      state.addEditInfo.isEdit = isEdit;
      state.addEditInfo.connectionInfo = connectionInfo;
      state.addEditInfo.catalogType = catalogType;
    },
    setIsEdit: (state, action) => {
      const { isEdit } = action.payload;
      state.addEditInfo.isEdit = isEdit;
    },
    setCatalogIdToEdit: (state, action) => {
      const { catalogId } = action.payload;
      state.addEditInfo.catalogId = catalogId;
    },
    setCatalogData: (state, action) => {
      const { data } = action.payload;
      state.addEditInfo.catalogData = data;
    },
    setCatalogDataField: (state, action) => {
      const { name, value } = action.payload;
      const newData = { ...state.addEditInfo.catalogData, [name]: value };
      state.addEditInfo.catalogData = newData;
    },
    resetCatalogData: (state) => {
      state.addEditInfo.catalogData = { ItemQualifiers: {} };
    },
    resetAddEditPopUp: (state) => {
      state.addEditInfo.isOpen = false;
      state.addEditInfo.isEdit = false;
      state.addEditInfo.connectionInfo = {
        connectionDisplayName: "",
        connectionId: -1,
      };
      state.addEditInfo.catalogType = -1;
      state.addEditInfo.catalogId = null;
      state.addEditInfo.catalogData = { ItemQualifiers: {} };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalogTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCatalogTypes.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchCatalogTypes.fulfilled, (state, action) => {
        state.status = "loaded";
        state.catalogTypes = action.payload.data;
      })
      .addCase(updateCatalogData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateCatalogData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(updateCatalogData.fulfilled, (state) => {
        state.status = "loaded";
        //state.catalogSchema = action.payload.data;
      })
      .addCase(deleteCatalogData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCatalogData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteCatalogData.fulfilled, (state) => {
        state.status = "loaded";
        //state.catalogSchema = action.payload.data;
      })
      .addCase(fetchCustomerCodes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCustomerCodes.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchCustomerCodes.fulfilled, (state, action) => {
        state.status = "loaded";
        state.addEditInfo.customers = action.payload.data;
      })
      .addCase(fetchItemCodes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchItemCodes.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchItemCodes.fulfilled, (state, action) => {
        state.status = "loaded";
        state.addEditInfo.items = action.payload.data;
      })
      .addCase(fetchAppType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAppType.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchAppType.fulfilled, (state, action) => {
        state.status = "loaded";
        state.appType = action.payload;
      })
      .addCase(fetchItemQualifiers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchItemQualifiers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchItemQualifiers.fulfilled, (state, action) => {
        state.status = "loaded";
        state.qualifiers = action.payload;
      })
      .addCase(checkIfHandlingEdi.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkIfHandlingEdi.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message ?? null;
      })
      .addCase(checkIfHandlingEdi.fulfilled, (state, action) => {
        state.status = "loaded";
        state.isEdiHandlingConnection = action.payload;
      });
  },
});

export const fetchCatalogTypes = createAsyncThunk(
  "manageCatalogData/fetchCatalogTypes",
  async () => {
    const response = await axios.get(
      "/connectioncatalogartifacts/get-catalog-types",
    );
    return response;
  },
);

export interface IUpdateCatalogDataAction {
  validateForm: () => IValidationForm;
  handleClose: () => void;
  dispatch: Dispatch;
  selectedConnection: number;
  catalogData: ICatalog;
  selectedCatalogType: number;
  artifactId: UUIDTypes;
  itemQualifiers: IItemQualifiers;
}
export const updateCatalogData = createAsyncThunk(
  "manageCatalogData/updateCatalogData",
  async ({
    validateForm,
    handleClose,
    dispatch,
    selectedConnection,
    catalogData,
    selectedCatalogType,
    artifactId,
    itemQualifiers, //optional parameter, used for item catalog
  }: IUpdateCatalogDataAction) => {
    const validation = validateForm();

    if (!validation.isValid) {
      dispatch(
        createAlert({
          status: "Error",
          message: validation.message,
        }),
      );
      return;
    }

    try {
      const response = await axios.put(
        "/connectioncatalogartifacts/update-catalog-data",
        {
          connectionId: selectedConnection,
          catalogType: selectedCatalogType,
          jsonData: catalogData,
          itemQualifiers: itemQualifiers ? itemQualifiers : undefined,
          catalogId: artifactId,
        },
      );
      if (response.data.error === true) {
        dispatch(
          createAlert({
            status: "Error",
            message: response.data.message,
          }),
        );
      } else {
        dispatch(
          createAlert({
            status: "Success",
            message: "Catalog updated successfully",
          }),
        );
      }
      handleClose();
      return response;
    } catch {
      dispatch(
        createAlert({
          status: "Error",
          message: "Error updating catalog",
        }),
      );
      handleClose();
    }
  },
);

export interface IDeleteCatalogDataResult {
  Deleted: number;
  TotalFailed: number;
  ExistingCrossRef: number;
  ExistingChildCrossRef: number;
  Message: string;
}

export const deleteCatalogData = createAsyncThunk<IDeleteCatalogDataResult, IDeleteCatalogDataAction, IThunkConfig>(
  "manageCatalogData/deleteCatalogData",
  async ({ selectedMappings }) => {
    const response = await axios.delete<IDeleteCatalogDataResult>(
      "/connectioncatalogartifacts/catalog-data",
      {
        headers: {
          "Content-Type": "application/json",
        },
        data: selectedMappings,
      },
    );
    return response.data;
  },
);

export interface IFetchCatalogsAction {
  selectedConnectionId: number;
  searchString?: string;
}

export const fetchCustomerCodes = createAsyncThunk(
  "manageCatalogData/fetchCustomerCodes",
  async ({ selectedConnectionId, searchString = "" }: IFetchCatalogsAction) => {
    const params = {
      connectionId: selectedConnectionId,
      searchString,
    }
    const response = await axios.get(
      "/connectioncatalogartifacts/get-customer-codes",
      {
        params
      },
    );
    return response;
  },
);

export const fetchItemCodes = createAsyncThunk(
  "manageCatalogData/fetchItemCodes",
  async ({ selectedConnectionId, searchString = "" }: IFetchCatalogsAction) => {
    const response = await axios.get(
      "/connectioncatalogartifacts/item-uom-codes",
      {
        params: {
          connectionId: selectedConnectionId,
          searchString,
        },
      },
    );
    return response;
  },
);

export const fetchAppType = createAsyncThunk(
  "manageCatalogData/fetchAppType",
  async (connectionId: number) => {
    const response = await axios.get("/connections/app-type", {
      params: {
        connectionId: connectionId,
      },
    });
    const appType = response.data;
    return appType;
  },
);

export interface IConnectionIdAction {
  connectionId: number
}
export const fetchItemQualifiers = createAsyncThunk(
  "manageCatalogData/fetchItemQualifiers",
  async ({ connectionId }: IConnectionIdAction, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "/connectionartifactschema/item-qualifiers",
        {
          params: { connectionId, artifactType: -1 },
        },
      );

      return response.data.values;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const checkIfHandlingEdi = createAsyncThunk(
  "manageCatalogData/checkIfHandlingEdi",
  async ({ connectionId }: IConnectionIdAction, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "/connectioncatalogartifacts/check-if-edi",
        {
          params: { connectionId },
        },
      );

      return response.data;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const handleAddCatalog = async (
  validateForm: () => IValidationForm,
  handleClose: () => void,
  dispatch: Dispatch,
  selectedConnection: number,
  selectedCatalogType: number,
  catalogData: string,
  itemQualifiers: string[], //optional parameter, used for item catalog
) => {
  const validation = validateForm();

  if (!validation.isValid) {
    dispatch(
      createAlert({
        status: "Error",
        message: validation.message,
      }),
    );
    return;
  }

  try {
    const response = await axios.post(
      "/connectioncatalogartifacts/add-catalog-data",
      {
        connectionId: selectedConnection,
        catalogType: selectedCatalogType,
        jsonData: catalogData,
        itemQualifiers: itemQualifiers ? itemQualifiers : undefined,
      },
    );
    if (response.data.error === true) {
      dispatch(
        createAlert({
          status: "Error",
          message: response.data.message,
        }),
      );
    } else {
      dispatch(
        createAlert({
          status: "Success",
          message: "Catalog added successfully",
        }),
      );
    }
    handleClose();
  } catch {
    dispatch(
      createAlert({
        status: "Error",
        message: "Error adding catalog",
      }),
    );
    handleClose();
  }
};

export const selectAppType = (state: RootState) =>
  state.tempInfo.manageCatalogData.appType;
export const selectConnection = (state: RootState) =>
  state.tempInfo.manageCatalogData.selectedConnection;
export const selectCatalogTypes = (state: RootState) =>
  state.tempInfo.manageCatalogData.catalogTypes;
export const selectCatalogType = (state: RootState) =>
  state.tempInfo.manageCatalogData.selectedCatalogType;
export const selectRows = (state: RootState) => state.tempInfo.manageCatalogData.rows;
export const selectSelectedRows = (state: RootState) =>
  state.tempInfo.manageCatalogData.selectedRows;
export const selectConnectionName = (state: RootState) =>
  state.tempInfo.manageCatalogData.connectionName;
export const selectConnectionDisplayName = (state: RootState) =>
  state.tempInfo.manageCatalogData.displayName;
export const selectEditCatalogData = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.catalogData;
export const selectEditCatalogId = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.catalogId;
export const selectIsEditMode = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.isEdit;
export const selectIsAddEditOpen = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.isOpen;
export const selectAddEditConnectionInfo = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.connectionInfo;
export const selectAvailableItems = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.items;
export const selectAvailableCustomers = (state: RootState) =>
  state.tempInfo.manageCatalogData.addEditInfo.customers;

export const {
  setConnection,
  setCatalogTypes,
  setCatalogType,
  setAppType,
  setRowsData,
  setSelectedRow,
  setConnectionName,
  setCustomer,
  setIsAddCatalogOpen,
  setCatalogData,
  resetCatalogData,
  setCatalogDataField,
  resetAddEditPopUp,
  setCatalogIdToEdit,
  setIsEdit,
} = manageCatalogDataSlice.actions;

export default manageCatalogDataSlice.reducer;
